import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Search, X, User, AlertTriangle } from 'lucide-react';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { usePatientFlags } from '@/services/scheduling/use-patient-flags';
import { usePatientSearch, type PatientSearchResult } from '@/services/scheduling/use-patient-search';
import {
  usePreBookSelectedPatient,
  toInvoicePrices,
  type PreBookSelectedPatientResponse,
} from '@/services/scheduling/use-prebook-selected-patient';
import { useAuthStore } from '@/auth/auth-store';
import type { AppointmentFormValues } from '@/types/appointment-form';
import { formatMrnLabel } from '@/shared/lib/mrn';

interface PatientSectionProps {
  /** Optional override for tests/stories. Defaults to the live patient query. */
  onSearchPatients?: (query: string) => Promise<PatientSearchResult[]>;
  /** Hook for "Create Patient" — opens the (TODO) inline patient-create flow. */
  onCreatePatient?: () => void;
  /**
   * Edit mode lock. Flutter `patient_widget.dart:148,269,276` hides the
   * clear/change controls AND the Create-Patient header whenever `isEdit`
   * is true — the patient is fixed for the life of an existing appointment,
   * regardless of its status. (The surrounding status `SectionLock` only
   * disables pointer events when the status advances past Booked/Proposed/
   * Pending, which would still leave a live × on a Booked appt.) When
   * `locked`, the clear × is removed entirely so there's no dead affordance.
   */
  locked?: boolean;
  /**
   * Seed for the patient-search box (Scheduling-AI command bar,
   * VITE_SCHEDULING_AI). Read ONCE as the search input's initial state —
   * the panel remounts per `formKey` when a new AI draft lands, so a new
   * seed always arrives on a fresh mount. The user searches/picks through
   * the EXISTING flow from there; nothing is auto-selected.
   */
  initialSearchText?: string;
}

export function PatientSection({
  onCreatePatient,
  locked = false,
  initialSearchText,
}: PatientSectionProps) {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const patientId = watch('patientId');
  const providerId = watch('providerId');
  const location = watch('location');
  const billingProfileId = watch('billingProfileId');
  const sponsorType = watch('sponsorType');
  const sponsorId = watch('sponsorId');
  const localFlags = watch('patientFlags');

  const [searchText, setSearchText] = useState(initialSearchText ?? '');
  const debouncedQuery = useDebounce(searchText);

  // Safety-net query — Flutter sources flags from PreBookSelectedPatient,
  // but that endpoint doesn't always ship `patientFlags` (e.g. when called
  // without a billingProfile). Falling back to /api/Appointment/PatientFlag
  // keeps the FLAGS card populated whenever the patient has flags on file.
  const { data: directFlags } = usePatientFlags(patientId);
  useEffect(() => {
    if (!directFlags?.length) return;
    if ((localFlags?.length ?? 0) > 0) return;
    const seen = new Set<string>();
    const unique: AppointmentFormValues['patientFlags'] = [];
    for (const f of directFlags) {
      const key = String(f.code ?? f.display ?? '').trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      unique.push({
        code: f.code,
        display: f.display,
        categoryName: f.category,
      });
    }
    if (unique.length === 0) return;
    setValue('patientFlags', unique);
    setValue(
      'flagIds',
      unique.map((f) => String(f.code ?? '')),
    );
    // localFlags is intentionally excluded — re-running on every form
    // edit would clobber picker writes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directFlags]);

  const orgId = useAuthStore((s) => s.user?.organizationId) ?? '';
  const { data: results = [], isFetching } = usePatientSearch({
    organizationId: orgId,
    query: debouncedQuery,
  });

  // PreBook fires when a patient is picked AND provider + location are
  // already set (Flutter create_appointment_widget.dart:1413–1417).
  const preBookPatient = usePreBookSelectedPatient();
  // Guards the PreBook trigger so the patient-pick handler (create) and the
  // edit-mode pre-seed effect never fire it twice for the same patient.
  const prebookedRef = useRef<string | null>(null);

  const selectPatient = (patient: PatientSearchResult) => {
    setValue('patientId', patient.id, { shouldValidate: true });
    setValue('patientName', `${patient.firstName} ${patient.lastName}`.trim());
    setValue('patientPhoto', patient.photo ?? patient.photoUrl ?? null);
    setValue('patientDob', patient.dateOfBirth ?? patient.birthDate ?? null);
    setValue('patientAge', patient.age ?? null);
    setValue('patientGender', patient.gender ?? null);
    setValue('patientImportanceCode', patient.importance?.code ?? null);
    setValue('patientIsMissingData', !!patient.isMissingData);
    // Flutter sets sponsorType to "Self-pay" on patient pick (line 1411).
    setValue('sponsorType', 'Self-Pay');
    setSearchText('');

    if (providerId && location?.id) {
      prebookedRef.current = patient.id;
      preBookPatient.mutate(
        {
          patientId: patient.id,
          providerId,
          locationId: location.id,
          billingProfileId,
          paymentType: (sponsorType ?? 'Self-Pay') === 'Self-Pay' ? 'Self-pay' : (sponsorType as 'Insurance' | 'Corporate'),
          sponsorId,
        },
        { onSuccess: applyPreBookResult },
      );
    }
  };

  // Apply a PreBookSelectedPatient response to the form (flags, invoice,
  // addresses, emergency contacts, insurance + corporate lists). Shared by
  // the patient-pick handler above and the edit-mode pre-seed effect below
  // (a hydrated patient never goes through `selectPatient`).
  function applyPreBookResult(response: PreBookSelectedPatientResponse) {
            // Auto-populate flags only when the local list is empty —
            // mirrors Flutter line 2305–2307. Backend ships duplicate
            // rows (one per encounter that recorded the flag); dedupe by
            // code so the picker and card render each flag once.
            if ((!localFlags || localFlags.length === 0) && response.patientFlags?.length) {
              const seenCodes = new Set<string>();
              const uniqueFlags: typeof response.patientFlags = [];
              for (const f of response.patientFlags) {
                const key = String(f.code ?? f.display ?? '').trim().toLowerCase();
                if (!key || seenCodes.has(key)) continue;
                seenCodes.add(key);
                uniqueFlags.push(f);
              }
              setValue(
                'patientFlags',
                uniqueFlags.map((f) => ({
                  code: f.code,
                  display: f.display,
                  categoryName: f.categoryName,
                })),
              );
              setValue(
                'flagIds',
                uniqueFlags.map((f) => String(f.code ?? '')),
              );
            }
            // Backend ships `prices` in the raw `CalculateSponsorAmount`
            // shape (gross / discount / net / payable / sponsorAmount);
            // normalise to Flutter's Invoice keys so the breakdown UI
            // shows real numbers instead of zeros (Flutter parity:
            // `payment_widget.dart:276-283`).
            const normalised = toInvoicePrices(response.prices);
            if (normalised) setValue('invoiceTotals', normalised);
            // Address list — drives the Residential Address section.
            // Default residentialAddressId to the first isDefault entry on
            // Home visits so the save rule passes without a manual pick
            // (Flutter calendar_screen.dart:2329–2339).
            //
            // Empty-fullAddress filter mirrors Flutter
            // `scheduling_bloc.dart:973-977` — entries where every address
            // part is blank (typically stub emergency contacts) are dropped
            // before they reach the picker AND before we pick the default.
            const rawAddresses = response.patientAddressList ?? [];
            const addresses = rawAddresses.filter(
              (a) =>
                [a.apartment, a.address, a.city, a.country]
                  .map((p) => (p ?? '').trim())
                  .some((p) => p.length > 0),
            );
            setValue('addressList', addresses);
            // Capture the patient's default address on every booking —
            // dev ships both `residentialAddressId` AND `residentialAddress`
            // regardless of location (the appointment may be at Office but
            // the patient's home-of-record still travels on the payload).
            // Without the id field the backend drops the address on save
            // and the GET response shows `residentialAddress: null`.
            const def = addresses.find((a) => a.isDefault) ?? addresses[0];
            const defId = def?.id ?? null;
            if (def) setValue('residentialAddress', def);
            if (defId)
              setValue('residentialAddressId', defId, { shouldValidate: true });

            // Emergency contacts — Flutter pulls from
            // `state.response.patient?.emergencyContactResponse`
            // (create_appointment_widget.dart:2326–2327). Older payloads
            // expose it under `emergencyContactDataList`; root-level
            // variants exist on some endpoints. Try all four.
            const contacts =
              response.patient?.emergencyContactResponse ??
              response.patient?.emergencyContactDataList ??
              response.emergencyContactResponse ??
              response.emergencyContactDataList ??
              [];
            setValue(
              'emergencyContacts',
              contacts.map((c) => {
                const fullName = [c.firstName, c.middleName, c.lastName]
                  .filter(Boolean)
                  .join(' ')
                  .trim();
                // Backend ships PascalCase keys on `relation` (Id/Name/...)
                // for existing rows; fall back to lowercase in case the
                // payload has already been normalised upstream.
                const rawRel = (c.relation ?? {}) as {
                  Id?: string; Name?: string; Code?: string; System?: string;
                  id?: string; name?: string; code?: string; system?: string;
                };
                const relName = rawRel.Name ?? rawRel.name;
                const relId = rawRel.Id ?? rawRel.id;
                // Also tolerate address fields shipped under PascalCase
                // (the dev backend mixes cases on this entity).
                const cAny = c as typeof c & {
                  Building?: string; Apartment?: string;
                  Country?: string; City?: string; Address?: string;
                  Latitude?: string; Longitude?: string;
                  SubmittedDate?: string;
                };
                const isSelfRow =
                  c.isSelf ?? (relName ?? '').toLowerCase() === 'self';
                return {
                  id: c.id ?? c.emergencyContactId,
                  emergencyContactId: c.emergencyContactId ?? c.id,
                  name: c.name || fullName,
                  firstName: c.firstName,
                  middleName: c.middleName,
                  lastName: c.lastName,
                  // Preserve the raw `relationship` (UUID for existing rows,
                  // string label otherwise) so the save endpoint can ship
                  // it back verbatim.
                  relationship: c.relationship ?? c.relationshipName,
                  relation: relId || relName
                    ? {
                        id: relId,
                        name: relName,
                        code: rawRel.Code ?? rawRel.code,
                        system: rawRel.System ?? rawRel.system,
                      }
                    : undefined,
                  phone: c.phone ?? c.contactNumber,
                  contactNumber: c.contactNumber ?? c.phone,
                  photo: c.photo ?? c.photoUrl,
                  photoUrl: c.photoUrl ?? c.photo,
                  isSelf: isSelfRow,
                  submittedDate: c.submittedDate ?? cAny.SubmittedDate ?? null,
                  building: c.building ?? cAny.Building ?? null,
                  apartment: c.apartment ?? cAny.Apartment ?? null,
                  country: c.country ?? cAny.Country ?? null,
                  city: c.city ?? cAny.City ?? null,
                  address: c.address ?? cAny.Address ?? null,
                  latitude: c.latitude ?? cAny.Latitude ?? null,
                  longitude: c.longitude ?? cAny.Longitude ?? null,
                };
              }),
            );

            // Insurance + Corporate sponsor lists — Flutter
            // `payment_widget.dart:151-195` reads them from the patient
            // object to populate the Insurance / Corporate carousels.
            setValue(
              'healthInsurances',
              response.patient?.healthInsuranceResponse ??
                response.healthInsuranceResponse ??
                [],
            );
            setValue(
              'corporateAgreements',
              response.patient?.corporateAgreementResponse ??
                response.corporateAgreementResponse ??
                [],
            );
  }

  // Edit-mode / pre-seeded patient — `selectPatient` never ran, so fire the
  // PreBook once the patient + provider + location are all present, to load
  // emergency contacts, insurance/corporate lists, addresses + invoice.
  useEffect(() => {
    if (!patientId || !providerId || !location?.id) return;
    if (prebookedRef.current === patientId) return;
    prebookedRef.current = patientId;
    preBookPatient.mutate(
      {
        patientId,
        providerId,
        locationId: location.id,
        billingProfileId,
        paymentType:
          (sponsorType ?? 'Self-Pay') === 'Self-Pay'
            ? 'Self-pay'
            : (sponsorType as 'Insurance' | 'Corporate'),
        sponsorId,
      },
      { onSuccess: applyPreBookResult },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, providerId, location?.id]);

  const clearPatient = () => {
    // Reset the PreBook guard — without this, clearing then re-picking the
    // SAME patient would skip PreBook (ref still === that id) and the
    // contacts / insurance / invoice wouldn't reload (Samer review on
    // PR #408 §3). The guard is keyed to patientId, so a DIFFERENT patient
    // re-fires regardless; this only covers the clear→same-patient path.
    prebookedRef.current = null;
    setValue('patientId', null);
    setValue('patientName', null);
    setValue('patientPhoto', null);
    setValue('patientDob', null);
    setValue('patientAge', null);
    setValue('patientGender', null);
    setValue('patientImportanceCode', null);
    setValue('patientIsMissingData', false);
    setValue('flagIds', []);
    setValue('patientFlags', []);
    setValue('invoiceTotals', null);
    setValue('emergencyContacts', []);
    setValue('callbackContactId', null);
    setValue('addressList', []);
    setValue('residentialAddressId', null, { shouldValidate: true });
    setValue('healthInsurances', []);
    setValue('corporateAgreements', []);
    setValue('sponsorType', 'Self-Pay');
    setValue('sponsorId', null);
    setValue('policyNo', null);
    setValue('sponsor', null);
  };

  return (
    <div className="px-6 py-4 border-t border-border-subtle relative">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-text-muted">
          Patient
        </label>
        {!locked && !patientId && onCreatePatient ? (
          <button
            type="button"
            onClick={onCreatePatient}
            className="text-sm font-medium text-brand-primary hover:text-interactive-hover"
          >
            Create Patient
          </button>
        ) : null}
      </div>

      <div className="mt-2 space-y-2">
        {patientId ? (
          // Locked (edit mode) → omit onClear so the × is hidden, mirroring
          // Flutter `patient_widget.dart:148` (`isEdit ? Container() : Row`).
          <SelectedPatientCard onClear={locked ? undefined : clearPatient} />
        ) : (
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-text-hint" />
            <Input
              placeholder="Search patient name, MRN, phone"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-8 h-10 text-sm"
            />
          </div>
        )}

        {!patientId && debouncedQuery.length >= 2 && (
          <div className="absolute left-6 right-6 z-30 mt-1 rounded-lg border border-border-default bg-surface-card shadow-lg max-h-[304px] overflow-y-auto scrollbar-hide">
            {isFetching && results.length === 0 ? (
              <p className="text-xs text-text-hint text-center py-3">Searching...</p>
            ) : results.length === 0 ? (
              <p className="text-sm text-text-muted text-center py-3">No matching patients.</p>
            ) : (
              results.map((patient) => (
                <button
                  key={patient.id}
                  type="button"
                  onClick={() => selectPatient(patient)}
                  className="w-full flex items-start gap-3 px-3 py-2.5 text-left hover:bg-grey-100 transition-colors"
                >
                  <Avatar className="h-10 w-10 flex-shrink-0">
                    <AvatarImage src={patient.photo ?? patient.photoUrl ?? undefined} />
                    <AvatarFallback className="text-size-2xs font-semibold bg-blue-100 text-brand-depth">
                      <User className="h-4 w-4" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-text-primary truncate">
                        {patient.firstName}
                        {patient.middleName ? ` ${patient.middleName}` : ''}{' '}
                        {patient.lastName}
                      </p>
                      {patient.isMissingData ? (
                        <AlertTriangle
                          className="h-4 w-4 text-status-warning flex-shrink-0"
                          aria-label="Missing data"
                        >
                          <title>Missing data</title>
                        </AlertTriangle>
                      ) : null}
                      {patient.importance?.code && patient.importance.code !== '' ? (
                        <Badge
                          variant="outline"
                          className="text-xs font-bold px-2 py-0 leading-snug border-status-success text-[var(--color-status-success-text)]"
                        >
                          {patient.importance.code}
                        </Badge>
                      ) : null}
                    </div>
                    <p className="text-xs text-text-muted truncate">
                      {[
                        formatPatientDob(patient.dateOfBirth ?? patient.birthDate, patient.age),
                        patient.gender,
                        patient.phone,
                        formatMrnLabel(patient.mrn),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        )}

      </div>
    </div>
  );
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Format the DOB row exactly like Flutter — "Oct 01, 1985 (40y)".
 * Flutter ships:
 *   - `dob` as a string ("01-Oct-1985" or "1985-10-01")
 *   - `age` as a pre-formatted string ("40y", "1y 4m 4d")
 * If the date can't be parsed, fall back to whatever the backend gave us.
 */
/** Selected-patient summary card — Flutter `patient_widget.dart:62–186`.
 *  `onClear` omitted → the clear × is hidden (locked / edit mode). */
function SelectedPatientCard({ onClear }: { onClear?: () => void }) {
  const { watch } = useFormContext<AppointmentFormValues>();
  const name = watch('patientName');
  const photo = watch('patientPhoto');
  const dob = watch('patientDob');
  const age = watch('patientAge');
  const gender = watch('patientGender');
  const importanceCode = watch('patientImportanceCode');
  const isMissingData = watch('patientIsMissingData');

  const initials = (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

  const subline = [
    formatPatientDob(dob ?? undefined, age ?? undefined),
    gender,
    importanceCode,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-border-default bg-surface-card">
      <Avatar className="h-10 w-10 flex-shrink-0">
        <AvatarImage src={photo ?? undefined} />
        <AvatarFallback className="text-size-2xs font-semibold bg-blue-100 text-brand-depth">
          {initials || <User className="h-4 w-4" />}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-text-primary truncate">{name}</p>
          {isMissingData ? (
            <AlertTriangle
              className="h-4 w-4 text-status-warning flex-shrink-0"
              aria-label="Missing data"
            >
              <title>Missing data</title>
            </AlertTriangle>
          ) : null}
        </div>
        <p className="text-xs text-text-muted truncate">{subline || '—'}</p>
      </div>
      {onClear ? (
        <button
          type="button"
          onClick={onClear}
          className="p-2 hover:bg-grey-100 rounded"
          aria-label="Clear patient"
        >
          <X className="h-4 w-4 text-status-error" />
        </button>
      ) : null}
    </div>
  );
}

function formatPatientDob(raw: string | undefined | null, age: string | undefined): string | null {
  if (!raw && !age) return null;
  if (!raw) return age ?? null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw + (age ? ` (${age})` : '');
  const month = MONTH_SHORT[d.getMonth()];
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  return `${month} ${day}, ${year}${age ? ` (${age})` : ''}`;
}
